import { gql } from '@apollo/client';

// QUERIES

export const QUERY_USER_GROUPS_BY_COMPANY_ID = gql`
	query QueryUserGroupsByCompanyIdIndex(
		$companyId: ID!
		$first: Int
		$after: String
	) {
		queryUserGroupsByCompanyIdIndex(
			companyId: $companyId
			first: $first
			after: $after
		) {
			items {
				id
				name
				active
			}
			nextToken
		}
	}
`;

export const GET_REWARDS_STORE_SETTINGS = gql`
	query GetRewardsStoreSettings($companyId: ID!) {
		getRewardsStoreSettings(companyId: $companyId) {
			companyId
			active
			campaignId
			pointsToCurrencyRatio
			availabilityGroups {
				id
				description
				companyId
				countryCodes
				userGroupIds
				name
			}
			tabSettings {
				rewardType
				active
				name
			}
		}
	}
`;

export const QUERY_REWARDS_STORE_TRANSACTIONS_BY_COMPANY_ID = gql`
	query QueryRewardsStoreTransactionsByCompanyId(
		$companyId: ID!
		$limit: Int
		$nextToken: String
	) {
		queryRewardsStoreTransactionsByCompanyId(
			companyId: $companyId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				amount
				companyId
				dateCreated
				id
				campaignId
				productId
				experienceId
				link
				rewardId
				snappyGiftId
				status
				userId
				user {
					id
					firstName
					lastName
					emailAddress
					employeeId
				}
				reward {
					id
					active
					amount
					companyId
					dateCreated
					externalId
					description
					image
					name
					rewardType
				}
			}
			nextToken
		}
	}
`;

export const QUERY_REWARDS_STORE_TRANSACTIONS_BY_USER_ID = gql`
	query QueryRewardsStoreTransactionsByUserId(
		$userId: ID!
		$companyId: ID!
		$limit: Int
		$nextToken: String
	) {
		queryRewardsStoreTransactionsByUserId(
			userId: $userId
			companyId: $companyId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				amount
				companyId
				dateCreated
				id
				campaignId
				productId
				experienceId
				link
				rewardId
				snappyGiftId
				status
				userId
				user {
					id
					firstName
					lastName
					emailAddress
					employeeId
				}
				reward {
					id
					active
					amount
					companyId
					externalId
					dateCreated
					description
					image
					name
					rewardType
				}
			}
			nextToken
		}
	}
`;

export const QUERY_REWARDS_STORE_REWARDS = gql`
	query QueryRewardsStoreRewardsByCompanyIdRewardType(
		$companyId: ID!
		$rewardType: RewardsStoreRewardType
		$sortOrder: SortDirection
		$limit: Int
		$nextToken: String
	) {
		queryRewardsStoreRewardsByCompanyIdRewardType(
			companyId: $companyId
			rewardType: $rewardType
			sortOrder: $sortOrder
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				active
				amount
				companyId
				availabilityGroups {
					companyId
					dateCreated
					description
					id
					name
					countryCodes
					userGroupIds
				}
				externalId
				dateCreated
				description
				id
				image
				name
				rewardType
				snappyCampaignId
				snappyProduct {
					id
					brand {
						id
						name
						description
					}
					budget
					category
					country
					coverImage
					description
					mediaItems
					options {
						beverageType
						color
						flavor
						takeYourPick
						size
						scent
					}
					title
					types
					variants {
						id
						budget
						brand {
							id
							name
							description
						}
						category
						country
						description
						optionAttributes {
							beverageType
							color
							flavor
							takeYourPick
							size
							scent
						}
						mediaItems
						title
						types
					}
				}
			}
			nextToken
		}
	}
`;

export const QUERY_REWARDS_STORE_REWARDS_AMOUNT = gql`
	query QueryRewardsStoreRewardsByCompanyIdRewardTypeAmount(
		$companyId: ID!
		$rewardType: RewardsStoreRewardType
		$sortOrder: SortDirection
		$limit: Int
		$nextToken: String
	) {
		queryRewardsStoreRewardsByCompanyIdRewardTypeAmount(
			companyId: $companyId
			rewardType: $rewardType
			sortOrder: $sortOrder
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				active
				amount
				companyId
				availabilityGroups {
					companyId
					dateCreated
					description
					id
					name
					countryCodes
					userGroupIds
				}
				externalId
				dateCreated
				description
				id
				image
				name
				rewardType
				snappyProduct {
					id
					brand {
						id
						name
						description
					}
					budget
					category
					country
					coverImage
					description
					mediaItems
					options {
						beverageType
						color
						flavor
						takeYourPick
						size
						scent
					}
					title
					types
					variants {
						id
						budget
						brand {
							id
							name
							description
						}
						category
						country
						description
						optionAttributes {
							beverageType
							color
							flavor
							takeYourPick
							size
							scent
						}
						mediaItems
						title
						types
					}
				}
			}
			nextToken
		}
	}
`;

export const CALL_SNAPPY_API = gql`
	query CallSnappyApi(
		$companyId: ID!
		$endpoint: String
		$queryParams: [QueryParameter]
		$method: String
		$stringifiedBody: String
	) {
		callSnappyApi(
			companyId: $companyId
			endpoint: $endpoint
			queryParams: $queryParams
			method: $method
			stringifiedBody: $stringifiedBody
		)
	}
`;

// MUTATIONS

export const CALL_SNAPPY_API_MUTATE = gql`
	mutation CallSnappyApiMutate(
		$companyId: ID!
		$endpoint: String
		$queryParams: [QueryParameter]
		$method: String
		$stringifiedBody: String
	) {
		callSnappyApiMutate(
			companyId: $companyId
			endpoint: $endpoint
			queryParams: $queryParams
			method: $method
			stringifiedBody: $stringifiedBody
		)
	}
`;

export const UPDATE_REWARDS_STORE_SETTINGS = gql`
	mutation UpdateRewardsStoreSettings($input: RewardsStoreSettingsInput!) {
		updateRewardsStoreSettings(input: $input) {
			companyId
			active
			campaignId
			pointsToCurrencyRatio
			availabilityGroups {
				id
				name
				dateCreated
				description
				companyId
				countryCodes
				userGroupIds
			}
			tabSettings {
				rewardType
				active
				name
			}
		}
	}
`;

export const CREATE_REWARDS_STORE_AVAILABILITY_GROUP = gql`
	mutation CreateRewardsStoreAvailabilityGroup(
		$input: CreateRewardsStoreAvailabilityGroupInput!
	) {
		createRewardsStoreAvailabilityGroup(input: $input) {
			id
			companyId
			countryCodes
			dateCreated
			description
			name
			userGroupIds
		}
	}
`;

export const UPDATE_REWARDS_STORE_AVAILABILITY_GROUP = gql`
	mutation UpdateRewardsStoreAvailabilityGroup(
		$input: UpdateRewardsStoreAvailabilityGroupInput!
	) {
		updateRewardsStoreAvailabilityGroup(input: $input) {
			id
			companyId
			countryCodes
			dateCreated
			description
			name
			userGroupIds
		}
	}
`;

export const DELETE_REWARDS_STORE_AVAILABILITY_GROUP = gql`
	mutation DeleteRewardsStoreAvailabilityGroup($companyId: ID!, $id: ID!) {
		deleteRewardsStoreAvailabilityGroup(companyId: $companyId, id: $id) {
			id
			companyId
			countryCodes
			dateCreated
			description
			name
			userGroupIds
		}
	}
`;

export const CREATE_REWARDS_STORE_REWARD = gql`
	mutation CreateRewardsStoreReward($input: CreateRewardsStoreRewardInput!) {
		createRewardsStoreReward(input: $input) {
			id
			companyId
			dateCreated
			description
			name
			active
			amount
			availabilityGroupIds
			availabilityGroups {
				companyId
				dateCreated
				description
				id
				countryCodes
				userGroupIds
				name
			}
			externalId
			image
			rewardType
			snappyProduct {
				id
				brand {
					id
					name
					description
				}
				budget
				category
				country
				coverImage
				description
				mediaItems
				options {
					beverageType
					color
					flavor
					takeYourPick
					size
					scent
				}
				title
				types
				variants {
					id
					budget
					brand {
						id
						name
						description
					}
					category
					country
					description
					optionAttributes {
						beverageType
						color
						flavor
						takeYourPick
						size
						scent
					}
					mediaItems
					title
					types
				}
			}
		}
	}
`;

export const UPDATE_REWARDS_STORE_REWARD = gql`
	mutation UpdateRewardsStoreReward($input: UpdateRewardsStoreRewardInput!) {
		updateRewardsStoreReward(input: $input) {
			id
			companyId
			dateCreated
			description
			name
			active
			amount
			availabilityGroupIds
			availabilityGroups {
				companyId
				dateCreated
				description
				id
				countryCodes
				userGroupIds
				name
			}
			externalId
			image
			rewardType
			snappyProduct {
				id
				brand {
					id
					name
					description
				}
				budget
				category
				country
				coverImage
				description
				mediaItems
				options {
					beverageType
					color
					flavor
					takeYourPick
					size
					scent
				}
				title
				types
				variants {
					id
					budget
					brand {
						id
						name
						description
					}
					category
					country
					description
					optionAttributes {
						beverageType
						color
						flavor
						takeYourPick
						size
						scent
					}
					mediaItems
					title
					types
				}
			}
		}
	}
`;

export const DELETE_REWARDS_STORE_REWARD = gql`
	mutation DeleteRewardsStoreReward($companyId: ID!, $id: ID!) {
		deleteRewardsStoreReward(companyId: $companyId, id: $id) {
			id
			companyId
			dateCreated
			description
			name
			active
			amount
			availabilityGroupIds
			availabilityGroups {
				companyId
				dateCreated
				description
				id
				countryCodes
				userGroupIds
				name
			}
			externalId
			image
			rewardType
			snappyProduct {
				id
				brand {
					id
					name
					description
				}
				budget
				category
				country
				coverImage
				description
				mediaItems
				options {
					beverageType
					color
					flavor
					takeYourPick
					size
					scent
				}
				title
				types
				variants {
					id
					budget
					brand {
						id
						name
						description
					}
					category
					country
					description
					optionAttributes {
						beverageType
						color
						flavor
						takeYourPick
						size
						scent
					}
					mediaItems
					title
					types
				}
			}
		}
	}
`;

export const CREATE_REWARDS_STORE_TRANSACTION = gql`
	mutation CreateRewardsStoreTransaction(
		$input: CreateRewardsStoreTransactionInput!
	) {
		createRewardsStoreTransaction(input: $input) {
			amount
			companyId
			dateCreated
			id
			campaignId
			productId
			experienceId
			link
			rewardId
			reward {
				active
				amount
				companyId
				dateCreated
				description
				externalId
				id
				name
				image
				rewardType
				availabilityGroups {
					companyId
					dateCreated
					description
					id
					countryCodes
					userGroupIds
					name
				}
			}
			status
			userId
			snappyGiftId
		}
	}
`;

export const UPDATE_REWARDS_STORE_TRANSACTION_STATUS = gql`
	mutation UpdateRewardsStoreTransactionStatus(
		$companyId: ID!
		$dateCreated: String!
		$id: ID!
		$status: RewardsStoreTransactionStatus!
	) {
		updateRewardsStoreTransactionStatus(
			companyId: $companyId
			dateCreated: $dateCreated
			id: $id
			status: $status
		) {
			userId
			status
			snappyGiftId
			rewardId
			id
			dateCreated
			companyId
			amount
		}
	}
`;

export const GET_AVAILABLE_POINTS = gql`
	query GetAvailablePoints($userId: ID!, $companyId: ID!) {
		getAvailablePoints(userId: $userId, companyId: $companyId)
	}
`;

export const CREATE_REWARDS_STORE_TRANSACTION_SNAPPY = gql`
	mutation CreateRewardsStoreTransactionSnappy(
		$input: RewardsStoreCheckoutInput
	) {
		createRewardsStoreTransactionSnappy(input: $input) {
			amount
			companyId
			dateCreated
			id
			campaignId
			productId
			experienceId
			link
			rewardId
			reward {
				active
				amount
				companyId
				dateCreated
				description
				externalId
				id
				name
				image
				rewardType
				availabilityGroups {
					companyId
					dateCreated
					description
					id
					countryCodes
					userGroupIds
					name
				}
			}
			status
			userId
			snappyGiftId
		}
	}
`;

// INPUT EXAMPLES

// const availabilityGroupInput = {
// 	companyId: "30abdd5d-5e53-4016-9495-7b7ec1444665",
// 	description: "Contains both default and external user groups.",
// 	name: "Default and External",
// 	userGroupIds: [
// 		"b8148e1c-3c54-43b2-8d6b-d362bfe812f1",
// 		"5279a2da-23ec-40bb-bf88-51ef7992a7bf"
// 	]
// }

// const rewardsStoreSettingsInput = {
// 	companyId: "30abdd5d-5e53-4016-9495-7b7ec1444665",
// 	active: true,
// 	pointsToCurrencyRatio: 100,
// 	tabSettings: [
// 		{ rewardType: gifts, active: true, name: "Gifts" },
// 		{ rewardType: giftCards, active: true, name: "Gift Cards" },
// 		{ rewardType: nonMonetary, active: true, name: "Non-Monetary" },
// 		{ rewardType: swag, active: true, name: "Swag" }
// 	]
// };

// const rewardsStoreRewardInput = {
// 	availabilityGroupIds: ["cae0646e-3bc3-4c67-b6bc-867588abfe07"],
// 	active: true,
// 	amount: 10000,
// 	companyId: "30abdd5d-5e53-4016-9495-7b7ec1444665",
// 	description: "100 Dollar gift card to Amazon.com",
// 	externalId: "no_external",
// 	image: "https://m.media-amazon.com/images/I/31x451MtRuL._SY445_SX342_QL70_FMwebp_.jpg",
// 	name: "$100 Amazon Gift Card",
// 	rewardType: giftCards
// };

// const rewardsStoreTransactionInput = {
// 	amount: 10000,
// 	companyId: "30abdd5d-5e53-4016-9495-7b7ec1444665",
// 	rewardId: "ee7bc0f8-3096-4fa5-92ec-80a5935d4f4a",
// 	snappyGiftId: "146bc5d7-dd98-478a-9aaf-450892e68a0a",
// 	userId: "618c0543-0ac0-40c9-8686-93737a8f1557"
// };

// const rewardsStoreCheckoutInput = {
// 	companyId: "30abdd5d-5e53-4016-9495-7b7ec1444665",
// 	userId: "618c0543-0ac0-40c9-8686-93737a8f1557",
// 	campaignId: "1234",
// 	productId: "1234",
// 	rewardId: "1234",
// 	amount: 10000,
// 	budget: 100,
// 	firstName: "Michael",
// 	lastName: "Baker",
// 	email: "mbaker@erinapp.com",
// 	phone: "12345678900"
// };
